/**
 * LoginPage — auto-redirects to WorkOS AuthKit when the auth backend is up
 * and the user has no session. When the backend is down (`degraded`), shows
 * a calm "service unavailable" message instead of an error toast that
 * looks like the user tried to sign in and failed.
 *
 * There is intentionally no separate "you've been signed out" screen — logout
 * lands here and immediately re-enters the sign-in flow. Single login flow.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Accept a `returnTo` query value only when it is a path on Martha's own site,
 * or an absolute URL on this window's origin (reduced to path + query).
 * Anything else (other origins, protocol-relative, backslashes, control
 * characters) is refused so the sign-in flow cannot be used as an open redirect.
 */
function acceptedReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  const isSitePath = (v: string) => v.startsWith('/') && !v.startsWith('//');
  if (isSitePath(raw)) return raw;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  const reduced = url.pathname + url.search;
  return isSitePath(reduced) ? reduced : null;
}

export default function LoginPage() {
  const { login, degraded, isLoading } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [userTriggered, setUserTriggered] = useState(false);

  const doLogin = (triggeredByUser = false) => {
    if (triggeredByUser) setUserTriggered(true);
    const returnTo =
      (location.state as { returnTo?: string } | null)?.returnTo ||
      acceptedReturnTo(new URLSearchParams(location.search).get('returnTo'));
    if (returnTo) sessionStorage.setItem('auth_return_to', returnTo);
    login(`${window.location.origin}/auth/callback`, 'google_oauth').catch((err) => {
      setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
    });
  };

  useEffect(() => {
    // Skip auto-trigger when:
    //   - bootstrap still in progress (would race with /api/auth/me)
    //   - auth backend is degraded (would just retry the same 5xx)
    if (isLoading || degraded) return;
    doLogin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, degraded]);

  // Auth-service unreachable / 5xx — calm message, no auto-retry
  if (degraded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8 text-center">
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Sign in temporarily unavailable</h2>
          <p className="text-sm text-neutral-500 mb-6">
            The authentication service is offline. Try again in a moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-lg transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Only show "Sign-in failed" when the user actually clicked Sign in / Try again.
  // Errors from the silent auto-trigger fall through to the spinner; the next
  // bootstrap will flip `degraded` if there's a real backend issue.
  if (error && userTriggered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8 text-center">
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Sign-in failed</h2>
          <p className="text-sm text-neutral-500 mb-6">{error}</p>
          <button
            onClick={() => doLogin(true)}
            className="inline-flex items-center px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-lg transition"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-neutral-500">Redirecting to sign in…</p>
      </div>
    </div>
  );
}
